import { useConsent } from "@packages/consent";
import { COOKIE_KEY_HLW_CONSENT, setCookie, useSession } from "@packages/sdk";
import { useCallback } from "react";

import type { PinSchema } from "../lib/schemas/pinSchema";
import { useRequestLoginRelatedAccountsPost } from "../requests/useRequestLoginRelatedAccountsPost";

export type UseSwitchProfilesProps = {
  onDismiss?: () => void;
  onSuccess?: (
    data: Awaited<
      ReturnType<
        ReturnType<typeof useRequestLoginRelatedAccountsPost>["mutateAsync"]
      >
    >,
  ) => void;
  wait?: boolean;
};

export const useSwitchProfiles = ({
  onSuccess,
  wait,
}: UseSwitchProfilesProps = {}) => {
  const { setConsent } = useConsent();
  const mutationPostLoginRelatedAccounts = useRequestLoginRelatedAccountsPost();
  const { setToken } = useSession();

  const switchProfiles = useCallback(
    async ({ pin, userId }: Partial<PinSchema> & { userId: number }) => {
      const response = await mutationPostLoginRelatedAccounts.mutateAsync({
        body: { pin },
        params: { path: { userId } },
      });

      if (response.oauth) {
        const newConsent = (response.user.consent as number[]) ?? null;
        // Write synchronously so the cookie is correct before window.location.reload()
        // fires — React's useEffect-based write may not run before the navigation.
        // Passing null deletes the cookie, letting ConsentProvider re-initialize from
        // geolocation rather than leaving an empty array that bypasses initialization.
        setCookie({ key: COOKIE_KEY_HLW_CONSENT, value: newConsent });
        setConsent(newConsent);
        setToken(response.oauth.access_token);

        if (onSuccess) onSuccess(response);
        else if (wait) return;
        else window.location.reload();
      }
    },
    [mutationPostLoginRelatedAccounts, onSuccess],
  );

  return { error: mutationPostLoginRelatedAccounts.error, switchProfiles };
};
